import React, { useEffect, useState, useCallback } from 'react';
import api from '@/utils/api';
import { Plus, Edit, Trash2, Tag, X, AlertTriangle, Power } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Backend `EXPIRATION_TYPES`. Mirror the spec at app/routes/coupons.py.
const EXPIRATION_OPTIONS = [
  { value: 'never', label: 'Never expires' },
  { value: 'one_time', label: 'One-time use (single redemption)' },
  { value: 'usage_limit', label: 'Limit total uses' },
  { value: 'time_range', label: 'Active during a date range' },
];

const DISCOUNT_OPTIONS = [
  { value: 'percent', label: 'Percent off (%)' },
  { value: 'flat', label: 'Flat amount off ($)' },
];

const COMMISSION_OPTION = { value: 'commission_percent', label: '% of Commission' };

const EMPTY_FORM = {
  code: '',
  discount_type: 'percent',
  amount: '',
  expiration_type: 'never',
  max_uses: '',
  starts_at: '',
  ends_at: '',
  is_active: true,
};

const fmtAmount = (c) => {
  if (c.discount_type === 'percent') return `${c.amount}% off`;
  if (c.discount_type === 'commission_percent') return `${c.amount}% of commission`;
  return `$${Number(c.amount).toFixed(2)} off`;
};

const fmtExpiry = (c) => {
  if (c.expiration_type === 'never') return 'No expiry';
  if (c.expiration_type === 'one_time') return 'One-time use';
  if (c.expiration_type === 'usage_limit')
    return `${c.used_count}/${c.max_uses ?? '∞'} used`;
  if (c.expiration_type === 'time_range') {
    const s = c.starts_at ? new Date(c.starts_at).toLocaleDateString() : '—';
    const e = c.ends_at ? new Date(c.ends_at).toLocaleDateString() : '—';
    return `${s} → ${e}`;
  }
  return '';
};

const toLocalDateTimeInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Strip seconds + tz so it fits a <input type="datetime-local"> value.
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // existing coupon doc or null
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [isFulfillment, setIsFulfillment] = useState(false);

  const discountOptions = isFulfillment ? [...DISCOUNT_OPTIONS, COMMISSION_OPTION] : DISCOUNT_OPTIONS;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/coupons');
      setCoupons(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load coupons');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.get('/tenant/subscription')
      .then((res) => setIsFulfillment(res.data?.subscription_type === 'fulfillment'))
      .catch(() => setIsFulfillment(false));
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code,
      discount_type: c.discount_type,
      amount: String(c.amount),
      expiration_type: c.expiration_type,
      max_uses: c.max_uses != null ? String(c.max_uses) : '',
      starts_at: toLocalDateTimeInput(c.starts_at),
      ends_at: toLocalDateTimeInput(c.ends_at),
      is_active: c.is_active,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const buildPayload = () => {
    const payload = {
      code: form.code.trim(),
      discount_type: form.discount_type,
      amount: Number(form.amount),
      expiration_type: form.expiration_type,
      is_active: !!form.is_active,
    };
    if (form.expiration_type === 'usage_limit') {
      payload.max_uses = Number(form.max_uses);
    } else if (form.expiration_type === 'one_time') {
      payload.max_uses = 1;
    } else if (form.expiration_type === 'time_range') {
      if (form.starts_at) payload.starts_at = new Date(form.starts_at).toISOString();
      if (form.ends_at) payload.ends_at = new Date(form.ends_at).toISOString();
    }
    return payload;
  };

  const validateClient = () => {
    if (!form.code.trim()) return 'Code is required';
    if (!/^[a-zA-Z0-9_-]{3,32}$/.test(form.code.trim()))
      return 'Code must be 3–32 chars (A–Z, 0–9, dash, underscore)';
    const amt = Number(form.amount);
    if (!Number.isFinite(amt) || amt <= 0) return 'Amount must be greater than 0';
    if ((form.discount_type === 'percent' || form.discount_type === 'commission_percent') && amt > 100)
      return `${form.discount_type === 'percent' ? 'Percent' : 'Commission percent'} discount cannot exceed 100%`;
    if (form.expiration_type === 'usage_limit') {
      const mu = Number(form.max_uses);
      if (!Number.isInteger(mu) || mu <= 0)
        return 'Max uses must be a whole number greater than 0';
    }
    if (form.expiration_type === 'time_range') {
      if (!form.starts_at && !form.ends_at)
        return 'Pick a start date, end date, or both';
      if (form.starts_at && form.ends_at) {
        if (new Date(form.starts_at) >= new Date(form.ends_at))
          return 'Start date must be before end date';
      }
    }
    return null;
  };

  const submitForm = async (e) => {
    e?.preventDefault?.();
    const err = validateClient();
    if (err) {
      toast.error(err);
      return;
    }
    try {
      setSaving(true);
      if (editing) {
        await api.put(`/admin/coupons/${editing.coupon_id}`, buildPayload());
        toast.success('Coupon updated');
      } else {
        await api.post('/admin/coupons', buildPayload());
        toast.success('Coupon created');
      }
      closeForm();
      load();
    } catch (err2) {
      toast.error(err2.response?.data?.detail || 'Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete coupon "${c.code}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/coupons/${c.coupon_id}`);
      toast.success('Coupon deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete coupon');
    }
  };

  const handleToggleActive = async (c) => {
    try {
      await api.put(`/admin/coupons/${c.coupon_id}`, { is_active: !c.is_active });
      toast.success(`Coupon ${!c.is_active ? 'activated' : 'paused'}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update coupon');
    }
  };

  return (
    <div data-testid="admin-coupons">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2
            className="text-2xl font-bold text-gray-900"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            Coupon Codes
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Run promotions for your storefront. Discounts apply to the cart subtotal
            (before tax/shipping). One coupon per order — coupons stack with Pro Shop Credit.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#4A6741] hover:bg-[#3A5733] text-white"
          data-testid="coupon-new-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Coupon
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#334155] border-r-transparent" />
        </div>
      ) : coupons.length === 0 ? (
        <div
          className="text-center py-16 bg-gray-50 border border-[#E8E4DC] rounded-lg"
          data-testid="coupons-empty"
        >
          <Tag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-700 font-medium">No coupons yet.</p>
          <p className="text-sm text-gray-500 mb-4">
            Create your first coupon to offer customers a discount at checkout.
          </p>
          <Button onClick={openCreate} className="bg-[#4A6741] hover:bg-[#3A5733] text-white">
            <Plus className="h-4 w-4 mr-2" />
            New Coupon
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#E8E4DC] rounded-lg">
          <table className="w-full text-sm" data-testid="coupons-table">
            <thead className="bg-gray-50 border-b border-[#E8E4DC]">
              <tr>
                <th className="text-left px-4 py-3 font-bold text-gray-700">Code</th>
                <th className="text-left px-4 py-3 font-bold text-gray-700">Discount</th>
                <th className="text-left px-4 py-3 font-bold text-gray-700">Validity</th>
                <th className="text-left px-4 py-3 font-bold text-gray-700">Status</th>
                <th className="text-right px-4 py-3 font-bold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F4F6]">
              {coupons.map((c) => (
                <tr
                  key={c.coupon_id}
                  className="hover:bg-gray-50/50"
                  data-testid={`coupon-row-${c.coupon_id}`}
                >
                  <td className="px-4 py-3 font-mono font-bold text-[#2C3E2D]">{c.code}</td>
                  <td className="px-4 py-3 text-gray-700">
                    {fmtAmount(c)}
                    {isFulfillment && c.discount_split_mode === 'shared' && (
                      <span
                        className="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full bg-blue-100 text-blue-700"
                        title="ImpactGolf co-funds half of this coupon's discount"
                        data-testid={`coupon-shared-badge-${c.coupon_id}`}
                      >
                        Shared with ImpactGolf
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{fmtExpiry(c)}</td>
                  <td className="px-4 py-3">
                    {c.is_active ? (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full bg-[#4A6741]/10 text-[#4A6741]">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full bg-gray-200 text-gray-600">
                        Paused
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <button
                      onClick={() => handleToggleActive(c)}
                      className="inline-flex items-center px-2 py-1 text-sm text-[#5A6B5C] hover:text-[#2C3E2D] hover:bg-gray-100 rounded transition-colors"
                      title={c.is_active ? 'Pause coupon' : 'Activate coupon'}
                      data-testid={`coupon-toggle-${c.coupon_id}`}
                    >
                      <Power className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openEdit(c)}
                      className="inline-flex items-center px-2 py-1 text-sm text-[#334155] hover:bg-gray-100 rounded transition-colors"
                      title="Edit coupon"
                      data-testid={`coupon-edit-${c.coupon_id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(c)}
                      className="inline-flex items-center px-2 py-1 text-sm text-[#EF4444] hover:bg-red-50 rounded transition-colors"
                      title="Delete coupon"
                      data-testid={`coupon-delete-${c.coupon_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
          onClick={closeForm}
          data-testid="coupon-modal-backdrop"
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="coupon-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {editing ? 'Edit Coupon' : 'New Coupon'}
              </h3>
              <button
                onClick={closeForm}
                disabled={saving}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                aria-label="Close"
                data-testid="coupon-modal-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={submitForm} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Coupon code
                </label>
                <Input
                  value={form.code}
                  onChange={(e) =>
                    setForm({ ...form, code: e.target.value.toUpperCase() })
                  }
                  placeholder="SUMMER10"
                  className="font-mono"
                  required
                  disabled={saving}
                  data-testid="coupon-form-code"
                />
                <p className="text-xs text-gray-500 mt-1">
                  3–32 characters · letters, numbers, dash, underscore
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount type
                  </label>
                  <Select
                    value={form.discount_type}
                    onValueChange={(v) => setForm({ ...form, discount_type: v })}
                    disabled={saving}
                  >
                    <SelectTrigger data-testid="coupon-form-discount-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {discountOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount {form.discount_type === 'flat' ? '($)' : '(%)'}
                  </label>
                  <Input
                    type="number"
                    min="0"
                    max={form.discount_type === 'flat' ? undefined : '100'}
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    required
                    disabled={saving}
                    data-testid="coupon-form-amount"
                  />
                  {form.discount_type === 'commission_percent' && (
                    <p className="text-xs text-gray-500 mt-1">
                      % of your profit share on the order, not % of price.
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration
                </label>
                <Select
                  value={form.expiration_type}
                  onValueChange={(v) => setForm({ ...form, expiration_type: v })}
                  disabled={saving}
                >
                  <SelectTrigger data-testid="coupon-form-expiration-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPIRATION_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {form.expiration_type === 'usage_limit' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max uses
                  </label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={form.max_uses}
                    onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                    required
                    disabled={saving}
                    data-testid="coupon-form-max-uses"
                  />
                </div>
              )}

              {form.expiration_type === 'time_range' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Starts at
                    </label>
                    <Input
                      type="datetime-local"
                      value={form.starts_at}
                      onChange={(e) =>
                        setForm({ ...form, starts_at: e.target.value })
                      }
                      disabled={saving}
                      data-testid="coupon-form-starts-at"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ends at
                    </label>
                    <Input
                      type="datetime-local"
                      value={form.ends_at}
                      onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                      disabled={saving}
                      data-testid="coupon-form-ends-at"
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  id="coupon-active"
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  disabled={saving}
                  className="h-4 w-4 accent-[#4A6741]"
                  data-testid="coupon-form-is-active"
                />
                <label htmlFor="coupon-active" className="text-sm text-gray-700">
                  Active (customers can apply this code at checkout)
                </label>
              </div>

              {editing && editing.used_count > 0 && (
                <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>
                    This coupon has already been used <b>{editing.used_count}</b>{' '}
                    time{editing.used_count === 1 ? '' : 's'}. Editing it won't
                    refund those orders.
                  </span>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-[#E8E4DC]">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={closeForm}
                  disabled={saving}
                  data-testid="coupon-form-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#4A6741] hover:bg-[#3A5733] text-white"
                  disabled={saving}
                  data-testid="coupon-form-submit"
                >
                  {saving
                    ? 'Saving…'
                    : editing
                    ? 'Save Changes'
                    : 'Create Coupon'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
